// Live-chat conversation card (thread + suggested reply).
import type { ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import type { LiveChatConversation } from '../mockData.js';

export interface ConversationCardProps {
  convo: LiveChatConversation;
  onCreateTicket: () => void;
  onTransfer: () => void;
  onEnd: () => void;
  onUseSuggested: () => void;
  /**
   * W37b: assignment pill. The design fixture passes its sample rep; a
   * real session passes the HubSpot thread's assignee (or "Unassigned").
   */
  assigned: { initials: string; label: string };
  /** W37b: status pill text ("Connected" in the fixture, the HubSpot thread status on a real session). */
  statusLabel: string;
  /** W37b: shown when the thread carries no messages (the queue read returns thread metadata only). */
  emptyThreadNote?: string;
}

export function ConversationCard({
  convo,
  onCreateTicket,
  onTransfer,
  onEnd,
  onUseSuggested,
  assigned,
  statusLabel,
  emptyThreadNote,
}: ConversationCardProps): ReactNode {
  return (
    <section className="card">
      <div className="head conversation-head">
        <div className="conversation-heading">
          <h2>Website chat</h2>
          <span className="pill green" id="thread-status">
            <i className="statusdot" />
            {statusLabel}
          </span>
          <span className="assigned" data-testid="live-chat-assigned">
            {assigned.initials ? <span className="mini">{assigned.initials}</span> : null}
            {assigned.label}
          </span>
        </div>
        <button className="teal" id="cs" type="button" onClick={onCreateTicket}>
          <Icon id="help" />
          Create CS Ticket
        </button>
        <div className="conversation-actions" role="group" aria-label="Conversation controls">
          <button id="transfer" type="button" onClick={onTransfer}>
            <Icon id="people" />
            Transfer
          </button>
          <button id="end" type="button" onClick={onEnd}>
            End chat
          </button>
        </div>
      </div>
      <div className="statebar" id="statebar" hidden />
      <div className="thread" id="thread">
        {convo.thread.length === 0 && emptyThreadNote ? (
          <small className="event" data-testid="live-chat-thread-empty">{emptyThreadNote}</small>
        ) : null}
        {convo.thread.map((entry, index) => (
          <div key={`${convo.id}-${index}`} className={`entry entry-${entry.kind}`}>
            {entry.kind === 'event' ? (
              <small className="event">{entry.body}</small>
            ) : (
              <>
                <div className="message-meta">
                  <strong>{entry.author}</strong>
                  <span>{entry.time}</span>
                </div>
                <div className={`bubble bubble-${entry.kind}`}>{entry.body}</div>
              </>
            )}
          </div>
        ))}
      </div>
      {convo.status === 'typing' ? (
        <div className="typing" id="typing">
          <div className="avatar person small" />
          <div className="typing-bubble">
            <i />
            <i />
            <i />
          </div>
        </div>
      ) : null}
      {convo.suggested ? (
        <div className="suggest">
          <header>
            <Icon id="lightning" />
            <strong>Suggested reply</strong>
            <span>· &nbsp; Review before sending</span>
          </header>
          <p id="suggested-text">{convo.suggested}</p>
          <button className="link" id="use-reply" type="button" onClick={onUseSuggested}>
            Use this reply
          </button>
        </div>
      ) : null}
    </section>
  );
}
